/**
 * Frequently Asked Questions Component with Advanced Interactivity
 * Features: Bookmarking, Related FAQs, PDF Export, Smart Search, Suggested Next
 */

import React, { useMemo, useState } from "react";
import "../FAQ.css";
import PortalHeader from "../PortalHeader";
import { FAQ_SECTIONS, highlightText, type FAQItem } from "./faqData";
import { exportFAQToPDF } from "./faqPdfExport";

const FAQ: React.FC = () => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>("amlCdd");
  const [searchTerm, setSearchTerm] = useState("");
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(new Set());
  const [viewedIds, setViewedIds] = useState<Set<string>>(new Set());

  const sections = FAQ_SECTIONS;

  // Flatten all FAQs for easier access
  const allFAQs = useMemo(() => {
    const flattened: Record<string, FAQItem> = {};
    sections.forEach((section) => {
      section.faqs.forEach((faq) => {
        flattened[faq.id] = faq;
      });
    });
    return flattened;
  }, [sections]);

  const renderHighlightedText = (text: string, term: string) => {
    const parts = highlightText(text, term);
    return parts.map((part, idx) =>
      part.type === "highlight" ? (
        <mark key={idx} className="faq-search-highlight">
          {part.text}
        </mark>
      ) : (
        <span key={idx}>{part.text}</span>
      ),
    );
  };

  // Get related FAQs
  const getRelatedFAQs = (faqId: string): FAQItem[] => {
    const faq = allFAQs[faqId];
    if (!faq || !faq.relatedIds) return [];

    return faq.relatedIds
      .map((id) => allFAQs[id])
      .filter((f) => f && f.id !== faqId)
      .slice(0, 2);
  };

  // Get suggested next FAQs based on viewed
  const getSuggestedNext = (): FAQItem[] => {
    if (viewedIds.size === 0) return [];

    const suggestedSet = new Set<string>();
    viewedIds.forEach((faqId) => {
      const faq = allFAQs[faqId];
      if (faq?.relatedIds) {
        faq.relatedIds.forEach((id) => suggestedSet.add(id));
      }
    });

    // Remove already viewed
    viewedIds.forEach((id) => suggestedSet.delete(id));

    return Array.from(suggestedSet)
      .map((id) => allFAQs[id])
      .filter((f) => f)
      .slice(0, 3);
  };

  const handleExportPDF = async () => {
    const currentSec = sections.find((s) => s.id === activeSection);
    if (!currentSec) return;
    try {
      await exportFAQToPDF(currentSec, filteredFAQs);
    } catch {
      alert("Error generating PDF. Please try again.");
    }
  };

  const toggleBookmark = (faqId: string) => {
    setBookmarkedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(faqId)) {
        newSet.delete(faqId);
      } else {
        newSet.add(faqId);
      }
      return newSet;
    });
  };

  const toggleExpand = (faqId: string) => {
    setExpandedId(expandedId === faqId ? null : faqId);
    if (expandedId !== faqId) {
      setViewedIds((prev) => new Set(prev).add(faqId));
    }
  };

  const currentSection = sections.find((s) => s.id === activeSection);

  // Filter FAQs based on search
  const filteredFAQs = useMemo(() => {
    if (!currentSection) return [];
    return currentSection.faqs.filter(
      (faq) =>
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [currentSection, searchTerm]);

  const suggestedNext = getSuggestedNext();

  return (
    <div className="faq-container">
      <PortalHeader />

      <div className="faq-wrapper">
        <div className="faq-header">
          <h1>Frequently Asked Questions</h1>
          <p className="faq-subtitle">
            AUSTRAC Regulatory Compliance Guide 2026
          </p>
          <p className="faq-count">
            Explore {sections.reduce((sum, s) => sum + s.faqs.length, 0)}{" "}
            comprehensive answers • {bookmarkedIds.size} bookmarked
          </p>
        </div>

        {/* Bookmarks Bar */}
        {bookmarkedIds.size > 0 && (
          <div className="faq-bookmarks-bar">
            <div className="faq-bookmarks-label">
              📌 Your Bookmarks ({bookmarkedIds.size})
            </div>
            <div className="faq-bookmarks-list">
              {Array.from(bookmarkedIds).map((id) => {
                const faq = allFAQs[id];
                return (
                  <button
                    key={id}
                    className="faq-bookmark-chip"
                    onClick={() => {
                      setExpandedId(id);
                      setViewedIds((prev) => new Set(prev).add(id));
                    }}
                  >
                    {faq?.question.substring(0, 50)}...
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="faq-search-bar">
          <input
            type="text"
            placeholder="Search questions and answers..."
            value={searchTerm}
            data-testid="faq-search-input"
            onChange={(e) => setSearchTerm(e.target.value)}
            className="faq-search-input"
          />
          <span className="faq-search-icon">🔍</span>
        </div>

        <div className="faq-main-container">
          {/* Section Tabs */}
          <div className="faq-tabs">
            {sections.map((section) => (
              <button
                key={section.id}
                className={`faq-tab ${activeSection === section.id ? "active" : ""}`}
                onClick={() => {
                  setActiveSection(section.id);
                  setSearchTerm("");
                  setExpandedId(null);
                }}
              >
                <span className="faq-tab-title">{section.title}</span>
                {section.faqs.filter((f) => bookmarkedIds.has(f.id)).length >
                  0 && (
                  <span className="faq-tab-badge">
                    {section.faqs.filter((f) => bookmarkedIds.has(f.id)).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* FAQs Content */}
          <div className="faq-content">
            {currentSection && (
              <>
                <div className="faq-section-header">
                  <div className="faq-section-title-bar">
                    <h2 className="faq-section-title">
                      {currentSection.title}
                    </h2>
                    <button
                      className="faq-export-btn"
                      data-testid="faq-export-pdf-btn"
                      onClick={handleExportPDF}
                      title="Export to PDF"
                    >
                      📥 Export
                    </button>
                  </div>
                  {currentSection.description && (
                    <p className="faq-section-description">
                      {currentSection.description}
                    </p>
                  )}
                </div>

                <div className="faq-list">
                  {filteredFAQs.length > 0 ? (
                    filteredFAQs.map((faq, index) => {
                      const relatedFAQs = getRelatedFAQs(faq.id);
                      const isBookmarked = bookmarkedIds.has(faq.id);
                      const isViewed = viewedIds.has(faq.id);

                      return (
                        <div
                          key={faq.id}
                          className={`faq-item ${expandedId === faq.id ? "expanded" : ""}`}
                          style={{
                            animationDelay: `${index * 50}ms`,
                          }}
                        >
                          <button
                            className="faq-question-btn"
                            onClick={() => toggleExpand(faq.id)}
                            aria-expanded={expandedId === faq.id}
                          >
                            <div className="faq-question-content">
                              <span className="faq-number">{index + 1}</span>
                              <div className="faq-question-text-wrapper">
                                <span className="faq-question-text">
                                  {renderHighlightedText(
                                    faq.question,
                                    searchTerm,
                                  )}
                                </span>
                                {isViewed && (
                                  <span className="faq-viewed-badge">
                                    ✓ Viewed
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="faq-question-actions">
                              <span
                                role="button"
                                tabIndex={0}
                                className={`faq-bookmark-icon ${isBookmarked ? "bookmarked" : ""}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleBookmark(faq.id);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleBookmark(faq.id);
                                  }
                                }}
                                title={
                                  isBookmarked
                                    ? "Remove bookmark"
                                    : "Bookmark this"
                                }
                              >
                                {isBookmarked ? "🔖" : "🔗"}
                              </span>
                              <span
                                className={`faq-chevron ${expandedId === faq.id ? "open" : ""}`}
                              >
                                ›
                              </span>
                            </div>
                          </button>

                          {expandedId === faq.id && (
                            <div className="faq-answer-container">
                              <div className="faq-answer">
                                <p>
                                  {renderHighlightedText(
                                    faq.answer,
                                    searchTerm,
                                  )}
                                </p>

                                {/* Tags */}
                                {faq.tags && faq.tags.length > 0 && (
                                  <div className="faq-tags">
                                    {faq.tags.map((tag) => (
                                      <span key={tag} className="faq-tag">
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                )}

                                {/* Related FAQs */}
                                {relatedFAQs.length > 0 && (
                                  <div className="faq-related">
                                    <h4>Related Questions:</h4>
                                    <ul className="faq-related-list">
                                      {relatedFAQs.map((relatedFaq) => (
                                        <li key={relatedFaq.id}>
                                          <button
                                            className="faq-related-link"
                                            onClick={() => {
                                              setExpandedId(relatedFaq.id);
                                              setViewedIds((prev) =>
                                                new Set(prev).add(
                                                  relatedFaq.id,
                                                ),
                                              );
                                            }}
                                          >
                                            {relatedFaq.question}
                                          </button>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="faq-no-results">
                      <p>No FAQs match your search. Try different keywords.</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Suggested Next Section */}
        {suggestedNext.length > 0 && (
          <div className="faq-suggested-section">
            <h3>💡 Users Also Asked</h3>
            <div className="faq-suggested-grid">
              {suggestedNext.map((faq) => (
                <div
                  key={faq.id}
                  className="faq-suggested-card"
                  onClick={() => {
                    setExpandedId(faq.id);
                    setViewedIds((prev) => new Set(prev).add(faq.id));
                  }}
                >
                  <h4>{faq.question}</h4>
                  <p>{faq.answer.substring(0, 80)}...</p>
                  <span className="faq-suggested-action">Read Answer →</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="faq-footer">
          <p>
            Need more information? Visit{" "}
            <a
              href="https://www.austrac.gov.au"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.austrac.gov.au
            </a>{" "}
            for official resources
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
